/**
 * 1.4.4 — Customer Profile 360. Agrega, por cliente, lo que ya existe ligado a él:
 * leads, proyectos, cotizaciones, citas, historial y recordatorios.
 *
 * Doble enforcement: el backend YA omite montos sin `financials:view` y enmascara
 * email/teléfono sin `contacts:view_pii`. La UI además OCULTA las columnas/bloques
 * financieros según `capabilities.canViewFinancials` que devuelve el endpoint.
 */
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { AttachmentsPanel } from './AttachmentsPanel';

const ACTIVITY_TYPES = [
  { id: 'NOTE', label: 'Nota', icon: '📝' },
  { id: 'CALL', label: 'Llamada', icon: '📞' },
  { id: 'EMAIL', label: 'Correo', icon: '✉️' },
  { id: 'MEETING', label: 'Reunión', icon: '🤝' },
];

const KIND_META: Record<string, { icon: string; label: string }> = {
  lead: { icon: '🎯', label: 'Lead' },
  project: { icon: '📁', label: 'Proyecto' },
  quote: { icon: '📄', label: 'Cotización' },
  appointment: { icon: '📅', label: 'Cita' },
  service_record: { icon: '🛠️', label: 'Servicio' },
};

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const btnGhost = 'rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('es-MX') : '—');
const fmtMoney = (v: any, currency = 'MXN') => {
  if (v == null) return '—';
  try { return new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(Number(v)); }
  catch { return `${Number(v).toFixed(2)} ${currency}`; }
};

function Section({ title, count, children }: { title: string; count: number; children: any }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <h3 className="font-serif text-base tracking-tight text-slate-900">{title}</h3>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{count}</span>
      </div>
      {count === 0
        ? <p className="px-5 py-4 text-sm text-slate-400">Sin registros.</p>
        : <div className="divide-y divide-slate-100">{children}</div>}
    </div>
  );
}

function Tile({ label, value }: { label: string; value: any }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</p>
      <p className="mt-1 font-serif text-xl tabular-nums text-slate-900">{value}</p>
    </div>
  );
}

const STATUS_CHIP = 'rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600';

/** §22 — Timeline cronológico unificado (eventos existentes + notas manuales). */
function Timeline({ contactId, canEdit }: { contactId: string; canEdit: boolean }) {
  const [events, setEvents] = useState<any[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [type, setType] = useState('NOTE');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    api<{ events: any[] }>(`/contacts/${contactId}/timeline`)
      .then((d) => setEvents(d.events))
      .catch((e: any) => setErr(e?.body?.message || e?.message || 'No se pudo cargar el timeline'));
  };
  useEffect(load, [contactId]);

  const addNote = async () => {
    if (!body.trim()) return;
    setSaving(true); setErr(null);
    try {
      await api(`/contacts/${contactId}/activities`, {
        method: 'POST',
        body: { activityType: type, body: body.trim() },
      });
      setBody('');
      load();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo guardar la nota');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <h3 className="font-serif text-base tracking-tight text-slate-900">Actividad (timeline)</h3>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{events?.length ?? 0}</span>
      </div>

      {canEdit && (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
          <select className="rounded-xl border border-slate-200 px-2 py-1.5 text-xs text-slate-700"
                  value={type} onChange={(e) => setType(e.target.value)}>
            {ACTIVITY_TYPES.map((t) => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
          </select>
          <input className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-1.5 text-sm"
                 placeholder="Agregar nota rápida…" value={body}
                 onChange={(e) => setBody(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }} />
          <button className="rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50"
                  onClick={addNote} disabled={saving || !body.trim()}>
            {saving ? 'Guardando…' : 'Agregar'}
          </button>
        </div>
      )}

      {err && <p className="px-5 py-2 text-xs text-rose-600">{err}</p>}

      {events == null
        ? <p className="px-5 py-4 text-sm text-slate-400">Cargando…</p>
        : events.length === 0
          ? <p className="px-5 py-4 text-sm text-slate-400">Sin actividad todavía.</p>
          : (
            <ol className="divide-y divide-slate-100">
              {events.map((e) => {
                const isActivity = e.kind === 'activity';
                const at = isActivity
                  ? ACTIVITY_TYPES.find((t) => t.id === e.activityType)
                  : null;
                const meta = isActivity
                  ? { icon: at?.icon ?? '📝', label: at?.label ?? 'Nota' }
                  : (KIND_META[e.kind] ?? { icon: '•', label: e.kind });
                return (
                  <li key={`${e.kind}-${e.id}`} className="flex items-start gap-3 px-5 py-3 text-sm">
                    <span className="mt-0.5 text-base leading-none">{meta.icon}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-slate-700"><span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{meta.label}</span> · {e.title}</p>
                      <p className="text-[11px] text-slate-400">
                        {fmtDateTime(e.occurredAt)}{e.status ? ` · ${e.status}` : ''}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
    </div>
  );
}

export function CustomerProfileView({ contactId, onBack, canEdit = false }: { contactId: string; onBack: () => void; canEdit?: boolean }) {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    api(`/contacts/${contactId}/profile`)
      .then((d) => { if (alive) setData(d); })
      .catch((e: any) => { if (alive) setErr(e?.body?.message || e?.message || 'No se pudo cargar el perfil'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [contactId]);

  if (loading) return <p className="text-sm text-slate-500">Cargando perfil…</p>;
  if (err) return (
    <div>
      <button className={btnGhost} onClick={onBack}>← Volver a Contactos</button>
      <p className="alert-over mt-4 rounded-xl px-4 py-3 text-sm">{err}</p>
    </div>
  );
  if (!data) return null;

  const { contact, capabilities, summary, leads, projects, quotes, appointments, serviceRecords, reminders } = data;
  const fin = !!capabilities?.canViewFinancials;
  const fullName = `${contact.firstName} ${contact.lastName ?? ''}`.trim();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button className={btnGhost} onClick={onBack}>← Volver a Contactos</button>
        {!capabilities?.canViewPii && (
          <span className="text-[11px] text-slate-400">Datos de contacto enmascarados (sin permiso PII)</span>
        )}
      </div>

      {/* Encabezado del cliente */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h1 className="font-serif text-2xl tracking-tight text-slate-900">{fullName}</h1>
        <p className="text-sm text-slate-500">
          {[contact.jobTitle, contact.company].filter(Boolean).join(' · ') || 'Cliente'}
          {contact.source ? ` · origen: ${contact.source}` : ''}
        </p>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
          <span>✉️ {contact.email ?? '—'}</span>
          <span>📞 {contact.phone ?? '—'}</span>
          <span className="text-slate-400">Cliente desde {fmtDate(contact.createdAt)}</span>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        <Tile label="Leads" value={summary.leadsCount} />
        <Tile label="Proyectos" value={summary.projectsCount} />
        <Tile label="Cotizaciones" value={summary.quotesCount} />
        <Tile label="Citas" value={summary.appointmentsCount} />
        <Tile label="Servicios" value={summary.serviceRecordsCount} />
        <Tile label="Última visita" value={summary.lastVisitAt ? fmtDate(summary.lastVisitAt) : '—'} />
      </div>
      {fin && summary.financials && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Tile label="Pipeline abierto" value={fmtMoney(summary.financials.openPipelineValue)} />
          <Tile label="Cotizaciones aceptadas" value={fmtMoney(summary.financials.acceptedQuotesTotal)} />
        </div>
      )}

      {/* Secciones */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Section title="Proyectos" count={projects.length}>
          {projects.map((p: any) => (
            <div key={p.id} className="flex items-center justify-between px-5 py-3 text-sm">
              <span className="text-slate-700">{p.name}</span>
              <span className="flex items-center gap-2">
                <span className={STATUS_CHIP}>{p.status}</span>
                {fin && p.budget != null && <span className="tabular-nums text-slate-500">{fmtMoney(p.budget, p.budgetCurrency)}</span>}
              </span>
            </div>
          ))}
        </Section>

        <Section title="Cotizaciones" count={quotes.length}>
          {quotes.map((q: any) => (
            <div key={q.id} className="flex items-center justify-between px-5 py-3 text-sm">
              <span className="text-slate-700">{q.code ?? q.id.slice(0, 8)}</span>
              <span className="flex items-center gap-2">
                <span className={STATUS_CHIP}>{q.status}</span>
                {fin && q.totalAmount != null && <span className="tabular-nums text-slate-500">{fmtMoney(q.totalAmount, q.currency)}</span>}
              </span>
            </div>
          ))}
        </Section>

        <Section title="Leads / Pipeline" count={leads.length}>
          {leads.map((l: any) => (
            <div key={l.id} className="flex items-center justify-between px-5 py-3 text-sm">
              <span className="text-slate-700">{l.title}</span>
              <span className="flex items-center gap-2">
                <span className={STATUS_CHIP}>{l.stage ?? l.status}</span>
                {fin && l.estimatedValue != null && <span className="tabular-nums text-slate-500">{fmtMoney(l.estimatedValue, l.currency)}</span>}
              </span>
            </div>
          ))}
        </Section>

        <Section title="Citas (Agenda)" count={appointments.length}>
          {appointments.map((a: any) => (
            <div key={a.id} className="flex items-center justify-between px-5 py-3 text-sm">
              <span className="text-slate-700">{a.serviceName ?? 'Servicio'} <span className="text-slate-400">· {fmtDate(a.scheduledAt)}</span></span>
              <span className="flex items-center gap-2">
                <span className={STATUS_CHIP}>{a.status}</span>
                {fin && a.priceCharged != null && <span className="tabular-nums text-slate-500">{fmtMoney(a.priceCharged, a.currency)}</span>}
              </span>
            </div>
          ))}
        </Section>

        <Section title="Historial de servicios" count={serviceRecords.length}>
          {serviceRecords.map((s: any) => (
            <div key={s.id} className="flex items-center justify-between px-5 py-3 text-sm">
              <span className="text-slate-700">{s.serviceName ?? 'Servicio'}</span>
              <span className="text-slate-400">{fmtDate(s.performedAt)}</span>
            </div>
          ))}
        </Section>

        <Section title="Recordatorios" count={reminders.length}>
          {reminders.map((r: any) => (
            <div key={r.id} className="flex items-center justify-between px-5 py-3 text-sm">
              <span className="text-slate-700">{r.message?.slice(0, 48) ?? r.reminderType}</span>
              <span className="flex items-center gap-2">
                <span className={STATUS_CHIP}>{r.status}</span>
                <span className="text-slate-400">{fmtDate(r.scheduledFor)}</span>
              </span>
            </div>
          ))}
        </Section>
      </div>

      {/* §22 Sprint A — Timeline cronológico unificado + adjuntos */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Timeline contactId={contactId} canEdit={canEdit} />
        <AttachmentsPanel owner="contact" ownerId={contactId} canEdit={canEdit} />
      </div>

      <p className="text-[11px] text-slate-400">
        Las compras POS (GastroGest) y los tickets de soporte no se ligan a un contacto
        todavía (sin FK) — quedan fuera del timeline. Follow-up §22.
      </p>
    </div>
  );
}
